import React from 'react';
import {useIntl} from 'react-intl';
import {usePopupCopyInspectionContext} from '../PopupCopyInspectionContext';
import {StyledFieldSelect} from '../../../_common/components/FieldSelect';
import FieldDatepicker from '../../../_common/components/FieldDatepicker';
import FieldFindCustomers from '../../../_common/components/FieldFindCustomers';
import PopupAddCustomer from '../../../CustomersEntity/components/PopupAddCustomer';
import {StyledFieldInput} from '../../../_common/components/FieldInput';
import {InputCheckbox} from '../../../_common/components/FieldCheckbox';
import {filterOptions} from '../../../_common/utils/options';
import {integerValue} from '../../../../../utils/format';
import {decisionTree} from '../../../_common/constants/decisionTree';
import Unit from '../../../_common/components/Unit';

const getLabel = (label, isMandatory, customStyles) => {
  return (
    <div className={customStyles}>
      {label}
      {isMandatory && <span className={'text-danger'}>*</span>}
    </div>
  );
};

const PopupForm = ({data}) => {
  const {formik, options} = usePopupCopyInspectionContext();
  const {values, setFieldValue} = formik;
  const {users, shumaMazminIdentities, banks, loanTypes, loanSubTypes} = options;
  const prefix = 'inspections-popup-copy';
  const {formatMessage} = useIntl();
  const commonStyles = 'mb-2 w-300px';
  const showLoanText = values.loanTypeID && `${values.loanTypeID.value}` === decisionTree.loanTypeID.another;

  return (
    <div>
      <div className={'d-flex justify-content-between mb-4'}>
        <Unit
          text={formatMessage({id: 'customers', defaultMessage: 'Customers'})}
          customClassnames={'w-50 mr-2'}
          icon={'/media/common/Group_pink.svg'}
        >
          <FieldFindCustomers
            name="customers"
            formik={formik}
            customLabel={
              <div className={'d-flex justify-content-between align-items-center w-100'}>
                <div>
                  {formatMessage({id: `${prefix}-form-customers`, defaultMessage: 'Responsible user'})}
                  <span className={'text-danger'}>*</span>
                </div>
                <PopupAddCustomer formikProps={formik}/>
              </div>
            }
            customItemStyles={'d-flex align-items-center'}
            customContainerStyles={'w-100'}
          />
        </Unit>
        <Unit
          text={formatMessage({id: `${prefix}-header-main`, defaultMessage: 'Main'})}
          customClassnames={'w-50 ml-2'}
          icon={'/media/common/User.svg'}
        >
          <div className={'d-flex flex-column justify-content-between align-items-center'}>
            <StyledFieldSelect
              fieldId={'responsibleUser'}
              value={values.responsibleUser}
              options={users}
              onChange={({value}) => {
                setFieldValue('responsibleUser', filterOptions(value)(users)[0]);
              }}
              label={getLabel(formatMessage({id: `${prefix}-form-user`, defaultMessage: 'Responsible user'}), true)}
              customContainerStyles={commonStyles}
            />
            <FieldDatepicker
              fieldId={'dueDate'}
              label={getLabel(formatMessage({id: `${prefix}-form-dueDate`, defaultMessage: 'Due date'}), false)}
              onChange={(date) => {
                setFieldValue('dueDate', new Date(date));
              }}
              customContainerStyles={commonStyles}
            />
            <StyledFieldSelect
              name={'shumaMazminIdentityID'}
              value={values.shumaMazminIdentityID}
              options={shumaMazminIdentities}
              onChange={({value}) => {
                setFieldValue('shumaMazminIdentityID', filterOptions(value)(shumaMazminIdentities)[0]);
              }}
              label={getLabel(formatMessage({
                id: `${prefix}-form-shumaMazminIdentityID`,
                defaultMessage: 'Identity'
              }), true)}
              customContainerStyles={commonStyles}
            />
          </div>
        </Unit>
      </div>
      <Unit
        text={formatMessage({id: 'tab-order-inspections-header-mainParameters', defaultMessage: 'Main parameters'})}
        icon={'/media/common/Decision-tree_orange.svg'}
      >
        {data.shumaReasonID === 2 && (
          <div className={'d-flex align-items-end mb-4'}>
            <StyledFieldSelect
              name={'bankID'}
              value={values.bankID}
              options={banks}
              onChange={({value}) => setFieldValue('bankID', filterOptions(value)(banks)[0])}
              label={getLabel(formatMessage({id: `${prefix}-field-label-bank`, defaultMessage: 'Bank'}), true)}
              customContainerStyles={'w-300px'}
            />
          </div>
        )}
        <div className={'d-flex justify-content-between align-items-center py-4'}>
          <StyledFieldInput
            fieldId={'bankBranchNumber'}
            label={getLabel(formatMessage({
              id: `${prefix}-field-label-bankBranchNumber`,
              defaultMessage: 'Bank branch number'
            }))}
            value={values.bankBranchNumber}
            onChange={e => setFieldValue('bankBranchNumber', integerValue(e))}
            customContainerStyles={`${commonStyles} mb-0`}
            icon={'/media/common/Number_primary.svg'}
          />
          <StyledFieldInput
            fieldId={'bankBranchName'}
            label={getLabel(formatMessage({
              id: `${prefix}-field-label-bankBranchName`,
              defaultMessage: 'Bank branch name'
            }))}
            value={values.bankBranchName}
            onChange={(e) => setFieldValue('bankBranchName', e.target.value)}
            customContainerStyles={`${commonStyles} mb-0`}
            icon={'/media/common/Bank_primary.svg'}
          />
          <StyledFieldInput
            fieldId={'bankClerkFullName'}
            label={getLabel(formatMessage({
              id: `${prefix}-field-label-bankClerkFullName`,
              defaultMessage: 'Bank clerk full name'
            }))}
            value={values.bankClerkFullName}
            onChange={(e) => setFieldValue('bankClerkFullName', e.target.value)}
            customContainerStyles={`${commonStyles} mb-0`}
            icon={'/media/common/User_primary.svg'}
          />
        </div>
        <div className={'d-flex justify-content-between align-items-center py-4'}>
          <StyledFieldInput
            fieldId={'bankEmail'}
            type={'email'}
            label={getLabel(formatMessage({id: `${prefix}-field-label-bankEmail`, defaultMessage: 'Bank email'}))}
            value={values.bankEmail}
            onChange={(e) => setFieldValue('bankEmail', e.target.value)}
            customContainerStyles={`${commonStyles} mb-0`}
            icon={'/media/common/Email_primary.svg'}
          />
          <StyledFieldInput
            fieldId={'bankRequestID'}
            label={getLabel(formatMessage({
              id: `${prefix}-field-label-bankRequestID`,
              defaultMessage: 'Bank request ID'
            }))}
            value={values.bankRequestID}
            onChange={(e) => setFieldValue('bankRequestID', e.target.value)}
            customContainerStyles={`${commonStyles} mb-0`}
            icon={'/media/common/Request_primary.svg'}
          />
          <StyledFieldInput
            fieldId={'bankBuildType'}
            label={getLabel(formatMessage({
              id: `${prefix}-field-label-bankBuildType`,
              defaultMessage: 'Bank build type'
            }))}
            value={values.bankBuildType}
            onChange={(e) => setFieldValue('bankBuildType', e.target.value)}
            customContainerStyles={`${commonStyles} mb-0`}
            icon={'/media/common/Type_primary.svg'}
          />
        </div>
        <div className={'d-flex justify-content-between align-items-center'}>
          <StyledFieldSelect
            name={'loanTypeID'}
            value={values.loanTypeID}
            options={loanTypes}
            onChange={({value}) => {
              setFieldValue('loanTypeID', filterOptions(value)(loanTypes)[0]);
            }}
            label={getLabel(formatMessage({id: `${prefix}-field-label-loanType`, defaultMessage: 'Loan type'}))}
            customContainerStyles={commonStyles}
          />
          <StyledFieldSelect
            name={'loanSubTypeID'}
            value={values.loanSubTypeID}
            options={loanSubTypes}
            onChange={({value}) => {
              setFieldValue('loanSubTypeID', filterOptions(value)(loanSubTypes)[0]);
            }}
            label={getLabel(formatMessage({id: `${prefix}-field-label-loanSubType`, defaultMessage: 'Loan sub type'}))}
            customContainerStyles={commonStyles}
          />
          {showLoanText ? (
            <StyledFieldInput
              fieldId={'loanText'}
              value={values.loanText}
              label={getLabel(formatMessage({id: `${prefix}-field-label-loanText`, defaultMessage: 'Loan text'}), true)}
              onChange={(e) => setFieldValue('loanText', e.target.value)}
              customContainerStyles={`${commonStyles} mb-0`}
              icon={'/media/common/Edit_primary.svg'}
            />
          ) : <div className={commonStyles}/>}
        </div>
        <div className={'d-flex justify-content-between align-items-center'}>
          <div
            onClick={() => setFieldValue('underConstruction', !values.underConstruction)}
          >
            <InputCheckbox
              fieldId={'underConstruction'}
              value={values.underConstruction}
              label={formatMessage({id: `${prefix}-form-underConstruction`, defaultMessage: 'Under construction'})}
              onChange={() => setFieldValue('underConstruction', !values.underConstruction)}
              customContainerStyles={'mx-2'}
            />
          </div>
        </div>
      </Unit>
    </div>
  );
};

export default PopupForm;